import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ColDef, GridApi, GridOptions, GridReadyEvent, IDatasource, IGetRowsParams } from '@ag-grid-community/core';
import { saveAs } from 'file-saver';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { CustomerService } from '../../../../core/modules/auth/services/customer.service';
import { IconCellRendererComponent } from '../../../../core/modules/grid-renderers/components/icon-cell-renderer/icon-cell-renderer.component';
import { LogDownloadCellRendererComponent } from '../../../../core/modules/grid-renderers/components/log-download-cell-renderer/log-download-cell-renderer.component';
import { LspEvents } from '../../../../core/services/analytics/analytics-events.constant';
import { AnalyticsEvent, AnalyticsService } from '../../../../core/services/analytics/analytics.service';
import { AuditLogService } from '../../../../core/services/audit-log/audit-log.service';
import { AuditLogProductCodes } from '../../../../core/services/audit-log/auditLog.model';
import { GridUtilitiesService } from '../../../../services/utilities/grid-utilities.service';
import { GuardianSyncJob } from '../../../guardians/interfaces/guardian-sync.model';
import { RosterSyncJob } from '../../../roster-sync-job/interfaces/roster-sync-jobs.model';
import { ListResponse, SyncHistoryService } from '../../services/sync-history.service';

interface DialogData {
  job: RosterSyncJob | GuardianSyncJob;
  type?: 'rosterSync' | 'guardianSync';
  deepLinkExecutionId?: string;
}

interface LogEntry {
  id?: string;
  name?: string;
  ownerId?: string;
  rosterChanges?: {
    add: string[];
    removal: string[];
  };
}

@Component({
  selector: 'app-sync-history-dialog',
  templateUrl: './sync-history-dialog.component.html',
  styleUrls: ['./sync-history-dialog.component.scss'],
})
export class SyncHistoryDialogComponent implements OnInit, OnDestroy {
  gridOptions: GridOptions;
  columnDefs: ColDef[];
  gridApi: GridApi;
  frameworkComponents;
  context;
  maxRowsFound = 0;
  jobName: string;
  summaryMessage: string;
  activeExecution: any;
  clickedExecution: any;

  view: 'list' | 'single' = 'list';
  loading = false;
  debug = false;
  noChangesDetected = false;
  showErrorsOnly = false;
  executionId: string;

  logContent: any[];

  private jobId: string;
  private customerId: string;
  private iconCellRendererTypes = {
    analysis: {
      icon: 'search',
      description: 'Analysis',
      iconClass: 'clr-blue',
    },
    execution: {
      icon: 'change_circle',
      description: 'Sync execution',
      iconClass: 'clr-green',
    },
    guardian_analysis: {
      icon: 'search',
      description: 'Guardian analysis',
      iconClass: 'clr-blue',
    },
    guardian_execution: {
      icon: 'change_circle',
      description: 'Guardian sync execution',
      iconClass: 'clr-green',
    },
    default: {
      icon: 'help_outline',
      description: 'Check logs',
    },
  };
  private fieldBoolean = 'AND';
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private gridUtils: GridUtilitiesService,
    private customerService: CustomerService,
    public syncHistory: SyncHistoryService,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private auditLogService: AuditLogService,
    private snackBar: MatSnackBar,
    private analyticsService: AnalyticsService,
  ) {
    this.syncHistory.setBusy('Loading...');
    this.context = { componentParent: this };

    this.gridOptions = {
      rowModelType: 'infinite',
      rowBuffer: 4000,
      animateRows: true,
      cacheBlockSize: 20,
      clipboardDelimiter: ',',
      suppressCopyRowsToClipboard: true,
      suppressFieldDotNotation: true,
      pagination: true,
      paginationPageSize: 20,
      rowHeight: 36,
      defaultColDef: {
        sortable: true,
        flex: 1,
      },

      statusBar: {
        statusPanels: [],
      },

      rowClassRules: {
        clicked: (params) => params?.data?.runId === params.context.componentParent?.clickedExecution,
      },
    };

    this.frameworkComponents = {
      logCellRenderer: LogDownloadCellRendererComponent,
      iconCellRenderer: IconCellRendererComponent,
    };

    this.columnDefs = [
      {
        field: 'type',
        headerName: 'Type',
        width: 70,
        maxWidth: 70,
        cellRenderer: 'iconCellRenderer',
        valueGetter: (params) => (params?.data ? this.iconCellRendererTypes[params?.data?.type || 'default'] : {}),
      },
      {
        field: 'jobName',
        headerName: 'Job name',
        hide: true,
      },
      {
        field: 'summary',
        headerName: 'Summary',
        valueFormatter: (params) => (params?.data ? params?.data?.summary?.message || 'View logs for details...' : ''),
      },
      {
        field: 'start',
        headerName: 'Start',
        width: 140,
        maxWidth: 140,
        valueFormatter: this.gridUtils.timestampValueFormatter,
      },
      {
        field: 'end',
        headerName: 'End',
        width: 140,
        maxWidth: 140,
        valueFormatter: this.gridUtils.timestampValueFormatter,
      },
      {
        field: 'errors',
        headerName: 'Errors?',
        width: 80,
        maxWidth: 80,
        cellClass: 'centered',
        valueFormatter: (params) => (params?.data ? (Object.keys(params?.data?.errors || {}).length ? 'Y' : 'N') : ''),
      },
      {
        field: '',
        headerName: 'Logs',
        width: 120,
        maxWidth: 120,
        cellClass: 'centered',
        cellRenderer: 'logCellRenderer',
        sortable: false,
        // valueFormatter: (params) => {
        //   console.log(params);
        //   return 'Download';
        // }
      },
    ];
  }

  ngOnInit(): void {
    this.summaryMessage = '';
    this.customerId = this.customerService.getCustomerId();
    this.jobName = this.dialogData.type === 'rosterSync' ? (this.dialogData.job as RosterSyncJob).name : 'Guardian Sync';
    this.jobId = this.dialogData.type === 'rosterSync' ? this.dialogData.job.id : 'GUARDIAN_SYNC';
    this.auditLogService.addLogEntry({
      productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
      typeId: this.dialogData.type === 'rosterSync' ? 21 : 8,
      actionId: this.dialogData.type === 'rosterSync' ? 148 : 196,
      logData: {
        jobName: this.jobName,
      },
    });
    this.syncHistory.refreshRows$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.gridApi.refreshInfiniteCache();
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.hideOverlay();
    const datasource = this.refreshRows();
    this.gridApi.setDatasource(datasource);
    this.gridApi.addEventListener('paginationChanged', () => {
      this.setLbLastRowOnPage();
      this.gridApi.refreshCells({ force: true });
    });
  }

  refreshRows(): IDatasource {
    const datasource = {
      getRows: (rowParams: IGetRowsParams) => {
        try {
          this.getRowData(rowParams).subscribe((listResponseString) => {
            this.syncHistory.clearBusy();
            // @ts-ignore
            const listResponse = JSON.parse(listResponseString);
            listResponse.data = listResponse.data.map((rowData) => ({
              ...rowData,
              deepLinkExecutionId: this.dialogData.deepLinkExecutionId,
            }));
            rowParams.successCallback(listResponse.data, listResponse.total);
            if (listResponse.total === 0) {
              this.gridApi.showNoRowsOverlay();
            }
            this.syncHistory.totalRows.next(listResponse.total);
            if (listResponse.total > this.maxRowsFound) {
              this.syncHistory.maxRows.next(listResponse.total);
              this.maxRowsFound = listResponse.total;
            }
          });
        } catch (error) {}
      },
    };
    return datasource;
  }

  backToList() {
    this.view = 'list';
    this.executionId = null;
    this.activeExecution = false;
  }

  loadSingleLog(data, burstCache = false, rerender = true) {
    this.view = 'single';
    this.loading = true;
    this.logContent = [];
    this.executionId = data.executionId || data.runId;
    this.clickedExecution = this.executionId;
    this.activeExecution = data;
    if (rerender) {
      this.gridApi.redrawRows();
    }

    this.showErrorsOnly = false;
    this.debug = false;
    this.summaryMessage = '';
    this.noChangesDetected = false;
    this.syncHistory
      .downloadLogFile({ customerId: data.customerId, jobId: data.jobId, executionId: this.executionId, burstCache })
      .pipe(
        take(1),
        switchMap((fileRefObservable) => fileRefObservable),
      )
      .subscribe((res: string) => {
        this.loading = false;
        this.logContent = Object.values(JSON.parse(res));
        if (this.jobId !== 'GUARDIAN_SYNC') {
          if (
            !this.activeExecution.summary?.teacherReview &&
            !this.activeExecution.summary?.classroomExceptions &&
            !this.activeExecution.summary?.rosterChanges?.add &&
            !this.activeExecution.summary?.rosterChanges?.remove
          ) {
            this.noChangesDetected = true;
          }
        } else if (this.activeExecution.summary.type === 'guardian_execution') {
          this.summaryMessage = `Guardian Execution`;
          // Note: This is partially here to handle legacy - there was a change made on the backend where we only get one action per resend now
          // But it's also nice getting all actions alphabetically by student
          const logContentLookup = this.logContent.reduce((accumulator, currentAction) => {
            if (!accumulator[currentAction.student]) {
              accumulator[currentAction.student] = {};
            }
            if (!accumulator[currentAction.student][currentAction.guardian]) {
              accumulator[currentAction.student][currentAction.guardian] = {};
            }
            accumulator[currentAction.student][currentAction.guardian][currentAction.action] = currentAction;
            return accumulator;
          }, {});
          this.logContent = Object.values(logContentLookup).reduce((accumulator: any[], studentActionsLookup) => {
            // If there is a withdraw and send event for the same student/guardian pair make sure they are tagged as resends and sort them together
            Object.values(studentActionsLookup).forEach(guardianActionsLookup => {
              const withdrawAction = guardianActionsLookup?.withdrawInvitation || guardianActionsLookup?.resendWithdrawInvitation;
              const sendAction = guardianActionsLookup?.sendInitation || guardianActionsLookup?.resendSendInvitation;
              if (Boolean(withdrawAction) && Boolean(sendAction)) {
                withdrawAction.action = 'resendWithdrawInvitation';
                sendAction.action = 'resendSendInvitation';
              }
              if (Boolean(withdrawAction)) {
                accumulator.push(withdrawAction);
              }
              if (Boolean(sendAction)) {
                accumulator.push(sendAction);
              }
              // Handle any other action types than withdraw and send (remove, for example)
              Object.values(guardianActionsLookup).forEach((currentAction: { action?: string }) => {
                if (!['withdrawInvitation', 'resendWithdrawInvitation', 'sendInitation', 'resendSendInvitation'].includes(currentAction?.action)) {
                  accumulator.push(currentAction);
                }
              });
            });
            return accumulator;
          }, []) as any[];
          if (!this.logContent.length) {
            this.summaryMessage += this.activeExecution.summary.status === 'Completed' ?
              ' - No actions were necessary during this sync.' :
              ` - Status: ${this.activeExecution.summary.status} - No actions were taken during this sync.`;
          }
        } else if (this.activeExecution.summary.type === 'guardian_analysis') {
          this.summaryMessage = `Guardian Analysis - `;
          const stats = this.activeExecution.summary.stats;
          const statsArray = _.reduce(
            stats,
            (arr, count, key) => {
              if (count) {
                arr.push(`${count} ${key.toLowerCase()}`);
              }
              return arr;
            },
            [],
          );

          if (statsArray?.length) {
            this.summaryMessage += statsArray.join(', ');
          } else {
            this.summaryMessage += this.activeExecution.summary.status === 'Completed' ?
            'No actions were necessary during this sync.' :
            `Status: ${this.activeExecution.summary.status} - No actions were taken during this sync.`;
          }
        } else if (
          !this.activeExecution.summary?.stats ||
          (!Object.keys(this.activeExecution.summary?.stats?.removeGuardians).length &&
            !Object.keys(this.activeExecution.summary?.stats?.sendInvitations).length &&
            !Object.keys(this.activeExecution.summary?.stats?.withdrawInvitations).length)
        ) {
          this.noChangesDetected = true;
        }
      });
  }

  async downloadLog() {
    this.analyticsService.sendEvent(LspEvents.lspEvents.jobs.downloadedSyncJobHistory as AnalyticsEvent);
    const content = JSON.stringify(this.logContent);
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `Little SIS Premium - ${this.jobId}_${this.executionId}_logfile.log`);
  }

  async refreshLog() {
    await this.loadSingleLog(this.activeExecution, true);
  }

  /**
   * Workaround to show the actual number of rows for a given page
   * Issue: https://github.com/ag-grid/ag-grid/issues/4295
   */
  private setLbLastRowOnPage(): void {
    // LSC-1099
    if (!this.gridApi) {
      return;
    }

    const lbLastRowOnPageEl = document.querySelector(`ag-grid-angular [ref='lbLastRowOnPage']`);
    if (lbLastRowOnPageEl) {
      const isLastPage = this.gridApi.paginationGetTotalPages() === this.gridApi.paginationGetCurrentPage() + 1;
      const endRow = isLastPage
        ? this.gridApi.paginationGetRowCount()
        : (this.gridApi.paginationGetCurrentPage() + 1) * this.gridApi.paginationGetPageSize();
      lbLastRowOnPageEl.innerHTML = endRow.toString();
    }
  }

  private getRowData(params): Observable<ListResponse> {
    const opts = { useCache: false, fieldBoolean: this.fieldBoolean };
    const req = {
      jobId: this.jobId,
      take: params.endRow - params.startRow,
      skip: params.startRow,
      filterModel: params.filterModel,
      sortModel: params.sortModel,
    };

    return this.syncHistory.find(req, opts).pipe(tap((response) => (this.fieldBoolean = 'AND')));
  }
}
